import { useEffect, useState } from "react";
import { agentApi, authApi, domainTemplateApi } from "../../../api/client";
import { COLORS } from "../../../theme/colors";
import { MATERIALS } from "../../../theme/materials";
import {
  CHUNK_PRESETS,
  DEFAULT_CHUNK_SIZE,
  DEFAULT_CHUNK_OVERLAP,
  DEFAULT_PRESET_ID,
  presetById,
  presetIdForValues,
} from "../../ingestion/chunkTuning";
import ChunkTuningGuide from "../../ingestion/ChunkTuningGuide";

// Phase B per agent-detail-viewer-v1.md §10 row 16. Per-agent settings
// GET + PUT (B12), embedding-model lock readout, and admin Migrate-
// Embeddings flow that reuses the existing embedding-migration runner.
//
// Processing Configuration (post-A9 follow-up): chunk presets + sliders +
// guide moved here from IngestionView so the per-agent Knowledge tab is
// the single home for ingestion-time settings. Writes to
// flow_config.ingestion.{preset, chunk_size, chunk_overlap} via the
// existing PUT /knowledge/settings ingestion payload.

const SETTINGS_KEYS = [
  { key: "enabled",            type: "bool",   label: "RAG enabled" },
  { key: "top_k",              type: "int",    label: "top_k" },
  { key: "score_threshold",    type: "float",  label: "score_threshold" },
  { key: "embedding_provider", type: "string", label: "embedding_provider" },
  { key: "embedding_model",    type: "string", label: "embedding_model" },
];

const inputStyle = {
  padding: "6px 10px", borderRadius: 8,
  border: `1px solid ${COLORS.border}`, background: COLORS.surface,
  color: COLORS.text, fontSize: 12, outline: "none", width: 180,
};

const Card = ({ title, children, action }) => (
  <div style={{
    ...MATERIALS.thickGlass(),
    padding: 16,
    borderRadius: 12,
    boxShadow: MATERIALS.spatialShadow(),
    display: "flex", flexDirection: "column", gap: 10,
  }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</div>
      {action}
    </div>
    {children}
  </div>
);

const Banner = ({ kind, children, onDismiss }) => {
  const color = kind === "error" ? COLORS.pink : COLORS.green;
  return (
    <div style={{
      padding: "8px 12px", borderRadius: 8,
      border: `1px solid ${color}55`, background: `${color}18`,
      color: COLORS.text, fontSize: 12,
      display: "flex", justifyContent: "space-between", gap: 12,
    }}>
      <span>{children}</span>
      {onDismiss && (
        <button onClick={onDismiss} style={{
          background: "transparent", border: "none", color: COLORS.textDim,
          cursor: "pointer", fontSize: 14,
        }}>{"✕"}</button>
      )}
    </div>
  );
};

// Exported for unit testing
export const parseValue = (raw, type) => {
  if (raw === "" || raw == null) return null;
  if (type === "int") {
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  }
  if (type === "float") {
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : null;
  }
  if (type === "bool") return Boolean(raw);
  return String(raw);
};

// Exported for unit testing
export const stringify = (v) => v == null ? "" : (typeof v === "boolean" ? v : String(v));

export default function KnowledgeSettingsPanel({ tree, currentUser }) {
  const agentId = tree?.agent?.id;
  const isDeveloper = ["owner", "administrator", "developer"].includes(currentUser?.role);
  const isAdmin = ["owner", "administrator"].includes(currentUser?.role);

  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState(null);

  // Migration flow state
  const [migState, setMigState] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migProvider, setMigProvider] = useState("");
  const [migModel, setMigModel] = useState("");

  // Domain template picker — drives ingestion-time entity extraction
  // (services/ingestion_engine) + knowledge subagent cypher tools
  // (services/supervisor/knowledge_subagent). Source-of-truth column:
  // agents.template; catalog: GET /api/domain-templates (marketplace).
  const [catalog, setCatalog] = useState([]);
  const [catalogErr, setCatalogErr] = useState(null);
  const [templateDraft, setTemplateDraft] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Processing Configuration — chunk presets + sliders + guide.
  // Writes to flow_config.ingestion.{preset,chunk_size,chunk_overlap}
  // via PUT /knowledge/settings (existing ingestion payload).
  const [chunkSize, setChunkSize] = useState(DEFAULT_CHUNK_SIZE);
  const [chunkOverlap, setChunkOverlap] = useState(DEFAULT_CHUNK_OVERLAP);
  const [presetId, setPresetId] = useState(DEFAULT_PRESET_ID);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [savingProcessing, setSavingProcessing] = useState(false);

  const reload = async () => {
    if (!agentId) return;
    setLoading(true);
    try {
      const res = await agentApi.getKnowledgeSettings(agentId);
      const d = res?.data || null;
      setData(d);
      const k = d?.knowledge || {};
      const fresh = {};
      for (const f of SETTINGS_KEYS) fresh[f.key] = stringify(k[f.key]);
      setDraft(fresh);
      setMigState(d?.embedding_migration_state || null);
      setTemplateDraft(d?.template || "generic");
      const ing = d?.ingestion || {};
      const cs = Number.isFinite(ing.chunk_size) ? ing.chunk_size : DEFAULT_CHUNK_SIZE;
      const co = Number.isFinite(ing.chunk_overlap) ? ing.chunk_overlap : DEFAULT_CHUNK_OVERLAP;
      setChunkSize(cs);
      setChunkOverlap(co);
      setPresetId(ing.preset || presetIdForValues(cs, co));
      setErr(null);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [agentId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await domainTemplateApi.list();
        if (cancelled) return;
        setCatalog(Array.isArray(res?.templates) ? res.templates : []);
        setCatalogErr(null);
      } catch (e) {
        if (!cancelled) setCatalogErr(e?.message || String(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Poll migration state every 10s while one is running.
  useEffect(() => {
    if (!agentId) return;
    const inFlight = migState && (migState.status === "running" || migState.status === "queued");
    if (!inFlight) return;
    const t = setInterval(async () => {
      try {
        const res = await authApi.getAgentEmbeddingMigration(agentId);
        const d = res?.data || res?.state || null;
        if (d) setMigState(d);
      } catch {}
    }, 10000);
    return () => clearInterval(t);
  }, [agentId, migState?.status]);

  const onChange = (key, val) => setDraft(prev => ({ ...prev, [key]: val }));

  const onSave = async () => {
    if (!agentId) return;
    setSaving(true);
    setErr(null);
    try {
      const knowledge = {};
      for (const f of SETTINGS_KEYS) {
        knowledge[f.key] = parseValue(draft[f.key], f.type);
      }
      const res = await agentApi.updateKnowledgeSettings(agentId, { knowledge });
      const d = res?.data || null;
      setData(d);
      setInfo("Settings saved.");
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const onPresetChange = (id) => {
    setPresetId(id);
    if (id === "custom") return;
    const p = presetById(id);
    setChunkSize(p.chunk_size);
    setChunkOverlap(p.chunk_overlap);
  };
  const onChunkSizeChange = (n) => {
    setChunkSize(n);
    setPresetId(presetIdForValues(n, chunkOverlap));
  };
  const onChunkOverlapChange = (n) => {
    setChunkOverlap(n);
    setPresetId(presetIdForValues(chunkSize, n));
  };

  const onSaveProcessing = async () => {
    if (!agentId) return;
    setSavingProcessing(true);
    setErr(null);
    try {
      const res = await agentApi.updateKnowledgeSettings(agentId, {
        ingestion: {
          preset: presetId,
          chunk_size: chunkSize,
          chunk_overlap: chunkOverlap,
        },
      });
      const d = res?.data || null;
      setData(d);
      setInfo("Processing configuration saved.");
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setSavingProcessing(false);
    }
  };

  const onSaveTemplate = async () => {
    if (!agentId) return;
    setSavingTemplate(true);
    setErr(null);
    try {
      const res = await agentApi.updateKnowledgeSettings(agentId, {
        template: templateDraft || "generic",
      });
      const d = res?.data || null;
      setData(d);
      setInfo("Domain template updated.");
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setSavingTemplate(false);
    }
  };

  const onMigrate = async () => {
    if (!agentId || !migProvider || !migModel) return;
    if (!confirm(`Migrate embeddings to ${migProvider}/${migModel}?\n\nVector search returns 423 until migration completes. Existing chunks are re-embedded; cost scales with corpus size.`)) return;
    setMigrating(true);
    setErr(null);
    try {
      await authApi.migrateAgentEmbeddings(agentId, migProvider, migModel);
      setInfo(`Migration kicked: ${migProvider}/${migModel}`);
      // Refresh state (which now should show queued/running).
      try {
        const res = await authApi.getAgentEmbeddingMigration(agentId);
        const d = res?.data || res?.state || null;
        if (d) setMigState(d);
      } catch {}
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setMigrating(false);
    }
  };

  const onCancelMigration = async () => {
    if (!agentId) return;
    if (!confirm("Cancel in-flight embedding migration?")) return;
    try {
      await authApi.cancelAgentEmbeddingMigration(agentId);
      setInfo("Migration cancelled.");
      await reload();
    } catch (e) {
      setErr(e?.message || String(e));
    }
  };

  if (loading) return <div style={{ color: COLORS.textDim }}>Loading settings…</div>;
  if (!data)   return <div style={{ color: COLORS.pink }}>{err || "Failed to load settings."}</div>;

  const knowledge = data.knowledge || {};
  const ingestion = data.ingestion || {};
  const lock = data.embedding_model_lock || "(none — first ingestion will set the lock)";
  const currentTemplate = data.template || "generic";
  const selectedMeta = catalog.find(t => t.id === templateDraft) || null;
  const dirtyTemplate = (templateDraft || "generic") !== (currentTemplate || "generic");
  const savedIng = data.ingestion || {};
  const dirtyProcessing = (
    chunkSize !== (Number.isFinite(savedIng.chunk_size) ? savedIng.chunk_size : DEFAULT_CHUNK_SIZE) ||
    chunkOverlap !== (Number.isFinite(savedIng.chunk_overlap) ? savedIng.chunk_overlap : DEFAULT_CHUNK_OVERLAP) ||
    presetId !== (savedIng.preset || presetIdForValues(
      Number.isFinite(savedIng.chunk_size) ? savedIng.chunk_size : DEFAULT_CHUNK_SIZE,
      Number.isFinite(savedIng.chunk_overlap) ? savedIng.chunk_overlap : DEFAULT_CHUNK_OVERLAP,
    ))
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {info && <Banner kind="success" onDismiss={() => setInfo(null)}>{info}</Banner>}
      {err && <Banner kind="error" onDismiss={() => setErr(null)}>{err}</Banner>}

      <Card title="Domain template" action={
        isDeveloper && (
          <button onClick={onSaveTemplate} disabled={savingTemplate || !dirtyTemplate} style={{
            padding: "5px 14px", borderRadius: 6, border: "none",
            background: dirtyTemplate ? COLORS.accent : COLORS.border,
            color: "#fff",
            fontSize: 11, fontWeight: 600,
            cursor: savingTemplate ? "wait" : (dirtyTemplate ? "pointer" : "default"),
            opacity: dirtyTemplate ? 1 : 0.6,
          }}>{savingTemplate ? "Saving…" : "Save template"}</button>
        )
      }>
        <div style={{ fontSize: 11, color: COLORS.textDim, lineHeight: 1.5 }}>
          The template's <span style={{ color: COLORS.cyan }}>domain grammar</span> drives entity
          + relationship extraction at ingestion time and the cypher tool
          set the knowledge subagent uses on the graph. Pick <code>generic</code>
          when no curated schema fits — the agent will use heuristic extraction.
        </div>
        {catalogErr && <div style={{ fontSize: 11, color: COLORS.pink }}>Catalog error: {catalogErr}</div>}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 10, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.04em" }}>template</span>
            <select
              value={templateDraft || "generic"}
              onChange={e => setTemplateDraft(e.target.value)}
              disabled={!isDeveloper}
              style={{ ...inputStyle, cursor: "pointer", minWidth: 280 }}
            >
              <option value="generic">generic — heuristic extraction (no curated schema)</option>
              {catalog.map(t => (
                <option key={t.id} value={t.id}>
                  {t.id} — {t.display_name || t.id}
                </option>
              ))}
            </select>
          </div>
        </div>
        {selectedMeta && (
          <div style={{
            padding: "8px 12px", borderRadius: 8,
            border: `1px solid ${COLORS.border}`, background: COLORS.surface,
            fontSize: 11, color: COLORS.text,
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: 8,
          }}>
            <div><span style={{ color: COLORS.textDim }}>entities:</span> <span style={{ color: COLORS.cyan }}>{selectedMeta.entity_count ?? 0}</span></div>
            <div><span style={{ color: COLORS.textDim }}>relationships:</span> <span style={{ color: COLORS.cyan }}>{selectedMeta.relationship_count ?? 0}</span></div>
            <div><span style={{ color: COLORS.textDim }}>cypher templates:</span> <span style={{ color: COLORS.cyan }}>{selectedMeta.cypher_template_count ?? 0}</span></div>
            <div><span style={{ color: COLORS.textDim }}>seed evals:</span> <span style={{ color: COLORS.cyan }}>{selectedMeta.seed_eval_question_count ?? 0}</span></div>
            {Array.isArray(selectedMeta.standards) && selectedMeta.standards.length > 0 && (
              <div style={{ gridColumn: "1 / -1" }}>
                <span style={{ color: COLORS.textDim }}>standards:</span>{" "}
                <span style={{ color: COLORS.text }}>{selectedMeta.standards.join(", ")}</span>
              </div>
            )}
            {selectedMeta.persona && (
              <div style={{ gridColumn: "1 / -1", color: COLORS.textDim, fontStyle: "italic" }}>
                {selectedMeta.persona}
              </div>
            )}
          </div>
        )}
      </Card>

      <Card title="Processing configuration" action={
        isDeveloper && (
          <button onClick={onSaveProcessing} disabled={savingProcessing || !dirtyProcessing} style={{
            padding: "5px 14px", borderRadius: 6, border: "none",
            background: dirtyProcessing ? COLORS.accent : COLORS.border,
            color: "#fff", fontSize: 11, fontWeight: 600,
            cursor: savingProcessing ? "wait" : (dirtyProcessing ? "pointer" : "default"),
            opacity: dirtyProcessing ? 1 : 0.6,
          }}>{savingProcessing ? "Saving…" : "Save as agent default"}</button>
        )
      }>
        <div style={{ fontSize: 11, color: COLORS.textDim, lineHeight: 1.5 }}>
          Chunk size + overlap drive how ingested documents are split before
          embedding. Pick a doc-type preset or fine-tune with the sliders.
          Saved values become this agent's default for every future upload.
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontSize: 10, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.04em" }}>document type</span>
          <select
            value={presetId}
            onChange={e => onPresetChange(e.target.value)}
            disabled={!isDeveloper}
            style={{ ...inputStyle, cursor: "pointer", width: "100%" }}
          >
            {CHUNK_PRESETS.map(p => (
              <option key={p.id} value={p.id}>
                {p.label} ({p.chunk_size.toLocaleString()} / {p.chunk_overlap})
              </option>
            ))}
            {presetId === "custom" && (
              <option value="custom">Custom ({chunkSize.toLocaleString()} / {chunkOverlap})</option>
            )}
          </select>
          <div style={{ fontSize: 11, color: COLORS.textDim }}>
            Picks both chunk-size and overlap together. Drag the sliders below to fine-tune.
          </div>
        </div>

        <div>
          <div style={{ fontSize: 10, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>
            Chunk size: <span style={{ color: COLORS.text, fontWeight: 700 }}>{chunkSize.toLocaleString()} chars</span>
          </div>
          <input
            type="range" min={200} max={3000} step={100} value={chunkSize}
            onChange={e => onChunkSizeChange(Number(e.target.value))}
            disabled={!isDeveloper}
            style={{ width: "100%", accentColor: COLORS.accent }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: COLORS.textDim }}>
            <span>200</span><span>3,000</span>
          </div>
        </div>

        <div>
          <div style={{ fontSize: 10, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>
            Chunk overlap: <span style={{ color: COLORS.text, fontWeight: 700 }}>{chunkOverlap} chars</span>
          </div>
          <input
            type="range" min={0} max={500} step={50} value={chunkOverlap}
            onChange={e => onChunkOverlapChange(Number(e.target.value))}
            disabled={!isDeveloper}
            style={{ width: "100%", accentColor: COLORS.accent }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: COLORS.textDim }}>
            <span>0</span><span>500</span>
          </div>
        </div>

        <div style={{
          background: COLORS.surface, border: `1px solid ${COLORS.border}`,
          borderRadius: 8, overflow: "hidden",
        }}>
          <button
            onClick={() => setShowRecommendations(v => !v)}
            style={{
              width: "100%", padding: "8px 12px", border: "none", cursor: "pointer",
              background: "transparent", display: "flex", alignItems: "center",
              justifyContent: "space-between", color: COLORS.text, fontSize: 11, fontWeight: 600,
            }}
          >
            <span>{showRecommendations ? "Hide" : "Show"} recommendations &amp; guide</span>
            <span style={{
              fontSize: 9, color: COLORS.textDim,
              transform: showRecommendations ? "rotate(180deg)" : "rotate(0)",
              transition: "transform 0.2s",
            }}>{"▼"}</span>
          </button>
          {showRecommendations && <ChunkTuningGuide />}
        </div>
      </Card>

      <Card title="Knowledge config" action={
        isDeveloper && (
          <button onClick={onSave} disabled={saving} style={{
            padding: "5px 14px", borderRadius: 6, border: "none",
            background: COLORS.accent, color: "#fff",
            fontSize: 11, fontWeight: 600, cursor: saving ? "wait" : "pointer",
          }}>{saving ? "Saving…" : "Save"}</button>
        )
      }>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          {SETTINGS_KEYS.map(f => (
            <div key={f.key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 10, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.04em" }}>{f.label}</span>
              {f.type === "bool" ? (
                <select value={draft[f.key] === "" ? "" : String(draft[f.key] === true || draft[f.key] === "true")}
                  onChange={e => onChange(f.key, e.target.value === "" ? "" : e.target.value === "true")}
                  disabled={!isDeveloper}
                  style={{ ...inputStyle, cursor: "pointer" }}
                >
                  <option value="">(unset)</option>
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              ) : (
                <input value={draft[f.key] ?? ""}
                  onChange={e => onChange(f.key, e.target.value)}
                  disabled={!isDeveloper}
                  type={f.type === "int" || f.type === "float" ? "number" : "text"}
                  step={f.type === "float" ? "0.01" : "1"}
                  placeholder={knowledge[f.key] == null ? "(unset)" : ""}
                  style={inputStyle} />
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card title="Embedding model">
        <div style={{ fontSize: 12, color: COLORS.text, fontFamily: "monospace" }}>
          lock: <span style={{ color: COLORS.cyan }}>{lock}</span>
        </div>
        <div style={{ fontSize: 11, color: COLORS.textDim, lineHeight: 1.5 }}>
          The lock identifies the model the agent's chunks were embedded with;
          vector search filters chunks by this value. Changing the lock
          requires re-embedding the entire corpus via the migration flow below.
        </div>

        {migState && (
          <div style={{
            padding: "8px 12px", borderRadius: 8,
            border: `1px solid ${COLORS.border}`, background: COLORS.surface,
            fontSize: 11, fontFamily: "monospace", color: COLORS.text,
          }}>
            <div>state: {migState.status || "—"}</div>
            {migState.target_provider && <div>target: {migState.target_provider}/{migState.target_model}</div>}
            {migState.total_chunks != null && (
              <div>progress: {migState.processed_chunks || 0} / {migState.total_chunks} chunks</div>
            )}
            {migState.error && <div style={{ color: COLORS.pink }}>error: {migState.error}</div>}
          </div>
        )}

        {isAdmin && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 10, color: COLORS.textDim, textTransform: "uppercase" }}>target provider</span>
              <input value={migProvider} onChange={e => setMigProvider(e.target.value)}
                placeholder="openai" style={{ ...inputStyle, width: 140 }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 10, color: COLORS.textDim, textTransform: "uppercase" }}>target model</span>
              <input value={migModel} onChange={e => setMigModel(e.target.value)}
                placeholder="text-embedding-3-large" style={{ ...inputStyle, width: 220 }} />
            </div>
            <button onClick={onMigrate}
              disabled={migrating || !migProvider || !migModel || migState?.status === "running" || migState?.status === "queued"}
              style={{
                padding: "8px 16px", borderRadius: 8, border: "none",
                background: COLORS.orange, color: "#fff",
                fontSize: 12, fontWeight: 600, cursor: migrating ? "wait" : "pointer",
                opacity: migrating || !migProvider || !migModel ? 0.6 : 1,
              }}>{migrating ? "Starting…" : "Migrate embeddings"}</button>
            {(migState?.status === "running" || migState?.status === "queued") && (
              <button onClick={onCancelMigration} style={{
                padding: "8px 14px", borderRadius: 8,
                border: `1px solid ${COLORS.pink}55`, background: "transparent",
                color: COLORS.pink, fontSize: 11, cursor: "pointer",
              }}>Cancel migration</button>
            )}
          </div>
        )}
      </Card>

      <Card title="Ingestion config (raw)">
        <pre style={{
          fontSize: 10, color: COLORS.text, fontFamily: "monospace",
          margin: 0, padding: 10, borderRadius: 8,
          border: `1px solid ${COLORS.border}`, background: COLORS.surface,
          overflow: "auto", maxHeight: 220,
        }}>{JSON.stringify(ingestion, null, 2) || "{}"}</pre>
      </Card>
    </div>
  );
}
